import { useState } from "react"
import type { Bid } from "@/lib/schemas"
import { useBidsStore } from "@/stores"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Trash, Loader2 } from "lucide-react"

interface DeleteBidDialogProps {
  bid: Bid
  trigger?: React.ReactNode
  onSuccess?: () => void
  // Controlled mode props
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function DeleteBidDialog({
  bid,
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
}: DeleteBidDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { deleteBid } = useBidsStore()

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await deleteBid(bid.id)
      setOpen(false)
      onSuccess?.()
    } catch (error) {
      // Error is handled by the store
      console.error("Failed to delete bid:", error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <AlertDialogTrigger asChild>
          {trigger ?? (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <Trash className="h-4 w-4" />
              Delete
            </Button>
          )}
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Bid</AlertDialogTitle>
          <AlertDialogDescription>
            Delete <strong>{bid.title}</strong>? It will be recoverable from
            Recently deleted for 30 days, then permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              void handleDelete()
            }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Bid
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
